import * as React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Box, Tooltip } from '@mui/material'

import { ScreenContext } from 'apps/utils/ScreenContext'
import { menu } from 'apps/common/utils'

import { useStyles } from './styles'

// Two-tone pentagon straddling a border, marking which side is the active
// tab: whichever half sits over the active (dark) button is bg-colored,
// the half over the inactive (light) button is ink-colored - each half is
// the inverse of the background it sits on. Height matches the nav row
// (64px, .footer-nav-btn) from base to apex.
// Width picked to match this exact clip-path's regular-pentagon proportions
// (same 260/247 ratio as the AboutUs domain pentagons and the AI page
// badge, which use this same polygon) - height is fixed at the button's
// 64px, so width follows from that ratio instead of being a free pick.
const MARKER_HEIGHT = 64
const MARKER_WIDTH = MARKER_HEIGHT * ( 260 / 247 )

const BorderMarker = ( { leftPercent, leftActive } ) => (
	<Box
		sx={ {
			position: 'absolute',
			bottom: 0,
			left: `${ leftPercent }%`,
			width: `${ MARKER_WIDTH }px`,
			height: `${ MARKER_HEIGHT }px`,
			transform: 'translateX(-50%)',
			clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)',
			// The colour split sits 1px off the geometric centre in favour of
			// the dark half: a light shape on dark reads wider than the same
			// dark shape on light, so an exact 50/50 looks off-centre. Which
			// half is dark depends on the theme, so the sign comes from
			// --marker-active-bias (base.scss): +1px widens the active
			// (bg-coloured) half in dark mode, -1px shrinks it in light mode.
			background: leftActive
				? 'linear-gradient(to right, var(--bg) calc(50% + var(--marker-active-bias)), var(--ink) 0)'
				: 'linear-gradient(to right, var(--ink) calc(50% - var(--marker-active-bias)), var(--bg) 0)',
			pointerEvents: 'none'
		} }
	/>
)

export const Footer = ( { navClassName } ) => {
	const classes = useStyles()
	const { isTablet } = React.useContext( ScreenContext )
	const { pathname } = useLocation()

	const items = [ ...menu ].reverse()
	const activeIndex = items.findIndex( ( { to } ) => pathname.startsWith( to ) )

	return (
		<Box paddingLeft={ isTablet ? '63px' : 0 }>
			<Box borderLeft={ isTablet ? '1px solid var(--line)' : 0 }>
				{ /* Horizontal nav strip mirroring the side panel (no logo button).
				     No borderTop here — the content grid above already draws that line. */ }
				<Box
					display='flex'
					maxWidth='1085px'
					borderLeft='1px solid var(--line)'
					position='relative'
					className={ navClassName }
				>
					{ items.map( ( { id, text, to } ) =>
						<NavLink
							key={ id }
							to={ to }
							activeClassName='selected'
							className='footer-nav-btn'
						>
							{ text }
						</NavLink>
					) }
					{ /* One marker per internal border (4, between 5 items), only
					     rendered where the active tab is one of the two neighbours. */ }
					{ activeIndex !== -1 && [ 0, 1, 2, 3 ].map( ( border ) => {
						if ( activeIndex !== border && activeIndex !== border + 1 ) return null
						return (
							<BorderMarker
								key={ border }
								leftPercent={ ( ( border + 1 ) / items.length ) * 100 }
								leftActive={ activeIndex === border }
							/>
						)
					} ) }
				</Box>
				<Box className={ classes.copyright }>
					<Box fontSize={ 14 } padding='8px 16px'>
						2016–{ new Date().getFullYear() } @{ ' ' }
						<Tooltip title='Please visit parent company website' arrow>
							<Box
								component='a'
								href='https://amtoss.com'
								target='_blank'
								rel='noopener noreferrer'
								sx={ { color: 'inherit', textDecoration: 'none', cursor: 'var(--cursor-pointer)' } }
							>
								AMTOSS
							</Box>
						</Tooltip>
						{ ' ' }/ ACT
						{ " > We're clean — zero cookies to your device from here < " }
					</Box>
				</Box>
			</Box>
		</Box>
	)
}
